'use client';

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { useConvexMutation } from '@/hooks/use-convex-mutation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BALL_LEVELS = [
  { value: 'red' as const, label: 'Red Ball', color: 'bg-red-500', textColor: 'text-white' },
  { value: 'orange' as const, label: 'Orange Ball', color: 'bg-orange-500', textColor: 'text-white' },
  { value: 'green' as const, label: 'Green Ball', color: 'bg-green-500', textColor: 'text-white' },
  { value: 'yellow' as const, label: 'Yellow Ball', color: 'bg-yellow-400', textColor: 'text-yellow-900' },
];

const CATEGORIES = [
  { value: 'technical' as const, label: 'Technical' },
  { value: 'tactical' as const, label: 'Tactical' },
  { value: 'physical' as const, label: 'Physical' },
  { value: 'mental' as const, label: 'Mental' },
];

type BallLevel = 'red' | 'orange' | 'green' | 'yellow';
type Category = 'technical' | 'tactical' | 'physical' | 'mental';

export function BallLevelCriteria() {
  const [selectedLevel, setSelectedLevel] = useState<BallLevel>('red');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<Category>('technical');

  const criteria = useQuery(api.director.evaluationCriteria.list, { ballLevel: selectedLevel });
  const createCriteria = useConvexMutation(api.director.evaluationCriteria.create);
  const updateCriteria = useConvexMutation(api.director.evaluationCriteria.update);
  const removeCriteria = useConvexMutation(api.director.evaluationCriteria.remove);

  const handleAdd = () => {
    setEditingId(null);
    setFormName('');
    setFormCategory('technical');
    setFormOpen(true);
  };

  const handleEdit = (item: { _id: string; name: string; category: string }) => {
    setEditingId(item._id);
    setFormName(item.name);
    setFormCategory(item.category as Category);
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    if (editingId) {
      await updateCriteria.mutateAsync({
        id: editingId as Id<"evaluationCriteria">,
        name: formName.trim(),
        category: formCategory,
      });
    } else {
      await createCriteria.mutateAsync({
        ballLevel: selectedLevel,
        name: formName.trim(),
        category: formCategory,
      });
    }
    setFormOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    await removeCriteria.mutateAsync({
      id: deleteConfirmId as Id<"evaluationCriteria">,
    });
    setDeleteConfirmId(null);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'technical': return 'bg-blue-100 text-blue-700';
      case 'tactical': return 'bg-purple-100 text-purple-700';
      case 'physical': return 'bg-green-100 text-green-700';
      case 'mental': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Ball Level Tabs */}
      <div className="flex gap-2">
        {BALL_LEVELS.map((bl) => (
          <button
            key={bl.value}
            onClick={() => setSelectedLevel(bl.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedLevel === bl.value
                ? `${bl.color} ${bl.textColor} shadow-md`
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {bl.label}
          </button>
        ))}
      </div>

      {/* Criteria List */}
      <div className="space-y-2">
        {criteria == null ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
        ) : criteria.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No custom criteria defined for {BALL_LEVELS.find(b => b.value === selectedLevel)?.label}.
            Players at this level will use the default 6 criteria.
          </p>
        ) : (
          criteria.map((item, index) => (
            <div
              key={item._id}
              className="flex items-center justify-between p-3 bg-muted/40 rounded-lg border border-border"
            >
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground w-5">{index + 1}.</span>
                <span className="font-medium text-foreground">{item.name}</span>
                <Badge className={getCategoryColor(item.category)}>
                  {item.category}
                </Badge>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(item)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteConfirmId(item._id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Button */}
      {(criteria?.length ?? 0) < 6 && (
        <Button
          variant="outline"
          onClick={handleAdd}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Criteria ({criteria?.length ?? 0}/6)
        </Button>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Criteria' : 'Add Criteria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="criteriaName">Name</Label>
              <Input
                id="criteriaName"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Forehand Technique"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="criteriaCategory">Category</Label>
              <Select value={formCategory} onValueChange={(v) => setFormCategory(v as Category)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createCriteria.isPending || updateCriteria.isPending}>
                {createCriteria.isPending || updateCriteria.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Criteria</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this evaluation criteria? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
